<template>
  <div class="overflow-x-auto min-h-[300px]">
    <table v-if="!isLoading && displayData.length > 0" class="min-w-full divide-y divide-gray-200">
      <TableHeader :columns="getColumns" />
      <tbody class="bg-white divide-y divide-gray-200">
        <SalaryRoleRow v-for="(role, index) in displayData" :key="role?.soc_code_id || 'role-' + index" :role="role"
          :url="formatRoleUrl(role)" />
      </tbody>
    </table>
    <div v-if="displayData.length === 0 && !isLoading" class="min-h-[250px] flex items-center justify-center">
      <TableEmptyState :message="`No salary data available${locationDisplay ? ` for ${locationDisplay}` : ''}`" />
    </div>
    <div v-if="isLoading" class="min-h-[250px] flex items-center justify-center">
      <TableLoading message="Loading salary data..." />
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import SalaryRoleRow from './SalaryRoleRow.vue'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import useRoleUrlFormatter from '~/composables/useRoleUrlFormatter'
import TableHeader from '~/components/ui/TableHeader.vue'
import TableEmptyState from '~/components/ui/TableEmptyState.vue'
import TableLoading from '~/components/ui/TableLoading.vue'
const props = defineProps({
  permalink: {
    type: String,
    required: true
  },
  selectedState: {
    type: String,
    default: ''
  },
  selectedCity: {
    type: String,
    default: ''
  },
  initialData: {
    type: Object,
    default: null
  },
  preFetchedData: {
    type: Object,
    default: null
  },
  processedData: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})
const stateRef = ref(props.selectedState)
const cityRef = ref(props.selectedCity)
const hasMounted = ref(false)
const locationDisplay = computed(() => {
  if (props.selectedCity && props.selectedState) {
    return `${props.selectedCity}, ${props.selectedState}`
  }
  if (props.selectedState) {
    return props.selectedState
  }
  return ''
})
const { activePeriod, displayPeriods, adjustValueForPeriod }
  = useGlobalSalaryPeriod()
const getColumns = computed(() => {
  const periodLabel
    = displayPeriods[activePeriod.value].charAt(0).toUpperCase()
    + displayPeriods[activePeriod.value].slice(1)
  if (activePeriod.value === 'hourly') {
    return [
      { title: 'Job Title' },
      { title: `Hourly Rate` },
      { title: 'Annual Equivalent' },
      { title: 'Sample Size' }
    ]
  } else if (activePeriod.value === 'annually') {
    return [
      { title: 'Job Title' },
      { title: 'Hourly Equivalent' },
      { title: `Annual Salary` },
      { title: 'Sample Size' }
    ]
  } else {
    return [
      { title: 'Job Title' },
      { title: `${periodLabel} Rate` },
      { title: 'Sample Size' }
    ]
  }
})
const prioritizeDeliveryDriver = (data: any[]): any[] => {
  const deliveryDriverIndex = data.findIndex(
    item => item.job_title?.toLowerCase() === 'delivery driver'
  )
  if (deliveryDriverIndex > 0) {
    const [deliveryDriver] = data.splice(deliveryDriverIndex, 1)
    data.unshift(deliveryDriver)
  }
  return data
}
const extractTableData = (data: any): any[] => {
  if (!data) return []
  if (Array.isArray(data)) {
    return prioritizeDeliveryDriver([...data])
  }
  if (data.success && Array.isArray(data.data)) {
    return prioritizeDeliveryDriver([...data.data])
  }
  if (data.success && data.data && Array.isArray(data.data.salaries)) {
    const salaries = [...data.data.salaries]
    prioritizeDeliveryDriver(salaries)
    return salaries.map(item => ({
      job_title: item.job_title,
      soc_code: item.soc_code || '',
      soc_code_id: item.soc_code_id || '',
      annual_pay: item.adjusted_salary?.P50 || 0,
      hourly_pay: (item.adjusted_salary?.P50 || 0) / 2080,
      count: item.count || 0
    }))
  }
  return []
}
const displayData = computed(() => {
  if (props.processedData && props.processedData.length > 0) {
    const data = [...props.processedData] as any[]
    return prioritizeDeliveryDriver(data)
  }
  const rawData = extractTableData(props.preFetchedData || props.initialData)
  return rawData.map(item => ({
    ...item,
    annual_pay:
      activePeriod.value === 'annually'
        ? item.annual_pay
        : adjustValueForPeriod(item.annual_pay),
    hourly_pay:
      activePeriod.value === 'hourly'
        ? item.hourly_pay
        : adjustValueForPeriod(item.annual_pay) / 40,
    period_pay: adjustValueForPeriod(item.annual_pay)
  }))
})
watch(activePeriod, () => { })
const { formatRoleUrl } = useRoleUrlFormatter(
  props.permalink,
  stateRef,
  cityRef
)
onMounted(() => {
  hasMounted.value = true
})
</script>